import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { formatDistanceToNow } from 'date-fns';
import React, { useState, useMemo } from 'react';
import { CgAttachment } from "react-icons/cg";
import { FiCalendar, FiEdit, FiTag } from 'react-icons/fi';
import { GoArrowDown, GoArrowUp } from "react-icons/go";
import { TiDeleteOutline } from "react-icons/ti";
import StatusBotIcon from "@/components/StatusBotIcon";

import CommentRenderer from './CommentRenderer';

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    PiCellSignalFullFill,
    PiCellSignalLowFill,
    PiCellSignalMediumFill,
    PiCellSignalNoneFill,
    PiUserCircleDashed
} from "react-icons/pi";
import {
    TbAlertCircle,
    TbCircle,
    TbCircleCheckFilled,
    TbCircleDashed,
    TbCircleHalf2
} from "react-icons/tb";

import { ActivityIcon, Sparkles } from "lucide-react";
import { RiEye2Line } from "react-icons/ri";
import { getIconComponent } from '@/lib/statusIconsMap';
import { STATUS_COLORS } from '@/lib/colorsMap';

const statusConfig = {
    backlog: { name: 'Backlog', icon: TbCircleDashed, color: 'text-gray-500' },
    todo: { name: 'Todo', icon: TbCircle, color: 'text-blue-500' },
    in_progress: { name: 'In Progress', icon: TbCircleHalf2, color: 'text-yellow-500' },
    in_review: { name: 'In Review', icon: RiEye2Line, color: 'text-violet-500' },
    done: { name: 'Done', icon: TbCircleCheckFilled, color: 'text-emerald-500' }
};

const priorityConfig = {
    no_priority: { name: 'No Priority', icon: PiCellSignalNoneFill, color: 'text-neutral-500' },
    low: { name: 'Low', icon: PiCellSignalLowFill, color: 'text-violet-500' },
    medium: { name: 'Medium', icon: PiCellSignalMediumFill, color: 'text-orange-500' },
    high: { name: 'High', icon: PiCellSignalFullFill, color: 'text-red-500' },
    urgent: { name: 'Urgent', icon: TbAlertCircle, color: 'text-red-500' }
};

// Default configuration for unknown activity types
const defaultConfig = {
    icon: (activity) => FiTag,
    getMessage: (activity) => `performed action: ${activity.type}`
};

// Add this helper function at the top of the file
const wrapInSpan = (text) => `<span class="font-medium text-neutral-700 dark:text-neutral-200">${text}</span>`;

// Add this helper function at the top with other helper functions
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function TaskActivityTimeline({ activities, uncombinedActivities, isLoading, availableStatuses }) {
    console.log('availableStatuses:', JSON.stringify(availableStatuses, null, 2));

    const statusConfig = useMemo(() => {
        const config = {};
        availableStatuses && availableStatuses.forEach(status => {
            config[status.id] = { name: status.name, icon: getIconComponent(status.icon) || TbCircle, color: STATUS_COLORS[status.color]?.text || 'text-muted-foreground' };
        });
        return config;
    }, [availableStatuses]);

    const activityConfig = {
        task_created: {
            icon: (activity) => {
                return function TaskCreatedActorIcon() {
                    return (
                        <div className="flex items-center">
                            <div className={`w-4 h-4 rounded-full ${activity.actor.avatarColor} flex items-center justify-center outline outline-white dark:outline-black relative z-10`}>
                                <span className="text-[0.6rem] font-medium text-white">
                                    {activity.actor.initials}
                                </span>
                            </div>
                            {activity.metadata.createdFromSubmission && 
                                <div className="-ml-1 w-4 h-4 bg-teal-600 rounded-full flex items-center justify-center outline outline-white dark:outline-black relative z-0">
                                    <StatusBotIcon className="w-2.5 h-2.5 text-white" />
                                </div>
                            }
                        </div>
                    );
                };
            },
            getMessage: (activity) => {
                return `created the task`;
            }
        },
        description_updated: {
            icon: (activity) => FiTag,
            getMessage: (activity) => `updated the description`
        },
        status_changed: {
            icon: (activity) => statusConfig[activity.metadata.to].icon,
            getMessage: (activity) => {
                if (activity.mergedCount > 1) {
                    return `changed the status from ${wrapInSpan(statusConfig[activity.metadata.from].name)} to ${wrapInSpan(statusConfig[activity.metadata.to].name)} (${activity.mergedCount} updates)`;
                }
                return `changed the status from ${wrapInSpan(statusConfig[activity.metadata.from].name)} to ${wrapInSpan(statusConfig[activity.metadata.to].name)}`;
            }
        },
        assignee_changed: {
            icon: (activity) => {
                if (!activity.metadata.toName) {
                    return PiUserCircleDashed;
                }
                return function AssigneeIcon() {
                    return (
                        <div className={`w-4 h-4 rounded-full ${activity.metadata.toAvatarColor} flex items-center justify-center`}>
                            <span className="text-[0.6rem] font-medium text-white">
                                {activity.metadata.toInitials}
                            </span>
                        </div>
                    );
                };
            },
            getMessage: (activity) => {
                if (!activity.metadata.toId) {
                    return `unassigned the task`;
                }
                if (activity.metadata.toId === activity.actor.id) {
                    return `self-assigned the task`;
                }
                return `assigned the task to ${wrapInSpan(activity.metadata.toName)}`;
            }
        },
        summary_updated: {
            icon: (activity) => FiEdit,
            getMessage: (activity) => `updated the summary`
        },
        priority_changed: {
            icon: (activity) => priorityConfig[activity.metadata.to.toLowerCase()].icon,
            getMessage: (activity) => {
                const priorityLevels = {
                    'urgent': 3,
                    'high': 2,
                    'medium': 1,
                    'low': 0,
                    'no_priority': -1
                };
                const fromLevel = priorityLevels[activity.metadata.from.toLowerCase()];
                const toLevel = priorityLevels[activity.metadata.to.toLowerCase()];
                const action = fromLevel < toLevel ? 'raised' : 'lowered';
                return `${action} the priority to ${wrapInSpan(priorityConfig[activity.metadata.to.toLowerCase()].name)}`;
            }
        },
        due_date_changed: {
            icon: (activity) => FiCalendar,
            getMessage: (activity) => {
                if (!activity.metadata.from && activity.metadata.to) {
                    return `set the due date to ${wrapInSpan(new Date(activity.metadata.to).toLocaleDateString())}`;
                }
                if (activity.metadata.from && !activity.metadata.to) {
                    return `removed the due date`;
                }
                if (activity.metadata.from && activity.metadata.to) {
                    const from = new Date(activity.metadata.from).toLocaleDateString();
                    const to = new Date(activity.metadata.to).toLocaleDateString();
                    return `changed the due date from ${wrapInSpan(from)} to ${wrapInSpan(to)}`;
                }
                return `updated the due date`;  // fallback case
            }
        },
        position_changed: {
            icon: (activity) => {
                if (activity.metadata.to < activity.metadata.from) {
                    return GoArrowUp;
                }
                return GoArrowDown;
            },
            getMessage: (activity) => {
                const fromPosition = activity.metadata.from;
                const toPosition = activity.metadata.to;
                const action = fromPosition > toPosition ? 'moved up' : 'moved down';
                return `${action} the task position`;
            }
        },
        attachment_added: {
            icon: (activity) => CgAttachment,
            getMessage: (activity) => {
                const fileSize = formatFileSize(activity.metadata.fileSize);
                return `attached <span class="font-medium text-neutral-700 dark:text-neutral-200">${activity.metadata.filename}</span> (${fileSize})`;
            }
        },
        attachment_removed: {
            icon: (activity) => TiDeleteOutline,
            getMessage: (activity) => {
                return `removed the attachment <span class="font-medium text-neutral-700 dark:text-neutral-200">${activity.metadata.filename}</span>`;
            }
        },
        label_added: {
            icon: (activity) => FiTag,
            getMessage: (activity) => {
                if (activity.metadata.labels) {
                    const labels = activity.metadata.labels
                        .filter(l => l.action === 'added')
                        .map(l => wrapInSpan(l.name));
                    if (labels.length === 0) return '';
                    return `added ${labels.length > 1 ? 'labels' : 'the label'} ${labels.join(', ')}`;
                }
                // Fallback for single label add
                const labelName = activity.metadata.labelName || activity.metadata.name;
                return labelName ? `added the label ${wrapInSpan(labelName)}` : 'added a label';
            }
        },
        label_removed: {
            icon: (activity) => FiTag,
            getMessage: (activity) => {
                if (activity.metadata.labels) {
                    const labels = activity.metadata.labels
                        .filter(l => l.action === 'removed')
                        .map(l => wrapInSpan(l.name));
                    if (labels.length === 0) return '';
                    return `removed ${labels.length > 1 ? 'labels' : 'the label'} ${labels.join(', ')}`;
                }
                // Fallback for single label removal
                const labelName = activity.metadata.labelName || activity.metadata.name;
                return labelName ? `removed the label ${wrapInSpan(labelName)}` : 'removed a label';
            }
        }
    };

    const [sortOrder, setSortOrder] = useState('asc');
    const [isCompact, setIsCompact] = useState(true);

    if (isLoading) {
        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="font-medium">Activity</h3>
                    <div className="flex items-center gap-2">
                        <div className="h-7 w-7 bg-muted animate-pulse rounded" />
                        <Separator orientation="vertical" className="h-5" />
                        <div className="h-7 w-24 bg-muted animate-pulse rounded" />
                    </div>
                </div>
                <div className="space-y-4">
                    {/* Two activities before comment */}
                    <div className="flex gap-2 pl-4">
                        <div className="flex-shrink-0">
                            <div className="w-4 h-4 bg-muted animate-pulse rounded-full" />
                        </div>
                        <div className="flex-grow">
                            <div className="flex gap-2 items-center">
                                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-48 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 pl-4">
                        <div className="flex-shrink-0">
                            <div className="w-4 h-4 bg-muted animate-pulse rounded-full" />
                        </div>
                        <div className="flex-grow">
                            <div className="flex gap-2 items-center">
                                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </div>
                        </div>
                    </div>

                    {/* Comment skeleton */}
                    <div className="bg-neutral-200/50 dark:bg-muted/40 rounded-lg p-4 space-y-2">
                        <div className="flex gap-2">
                            <div className="w-4 h-4 rounded-full bg-muted animate-pulse" />
                            <div className="flex items-baseline gap-2">
                                <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className="h-4 w-full bg-muted animate-pulse rounded" />
                            <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
                        </div>
                    </div>

                    {/* Three activities after comment */}
                    <div className="flex gap-2 pl-4">
                        <div className="flex-shrink-0">
                            <div className="w-4 h-4 bg-muted animate-pulse rounded-full" />
                        </div>
                        <div className="flex-grow">
                            <div className="flex gap-2 items-center">
                                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-64 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 pl-4">
                        <div className="flex-shrink-0">
                            <div className="w-4 h-4 bg-muted animate-pulse rounded-full" />
                        </div>
                        <div className="flex-grow">
                            <div className="flex gap-2 items-center">
                                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-40 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 pl-4">
                        <div className="flex-shrink-0">
                            <div className="w-4 h-4 bg-muted animate-pulse rounded-full" />
                        </div>
                        <div className="flex-grow">
                            <div className="flex gap-2 items-center">
                                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-56 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const sortedActivities = (isCompact ? activities : uncombinedActivities).slice().sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="font-medium">Activity</h3>
                <div className="flex items-center gap-2">
                    <TooltipProvider delayDuration={0}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setIsCompact(!isCompact)}
                                    className="h-7 px-2"
                                >
                                    {isCompact ? (
                                        <Sparkles className="h-3.5 w-3.5" />
                                    ) : (
                                        <ActivityIcon className="h-3.5 w-3.5" />
                                    )}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                {isCompact ? "Show all activities" : "Summarize activities"}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <Separator orientation="vertical" className="h-5"  />
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        className="text-xs h-7 px-1.5"
                    >
                        {sortOrder === 'asc' ? (
                            <GoArrowUp className="h-4 w-4 mr-1" />
                        ) : (
                            <GoArrowDown className="h-4 w-4 mr-1" />
                        )}
                        {sortOrder === 'asc' ? 'Oldest first' : 'Newest first'}
                    </Button>
                </div>
            </div>
            <div className="space-y-4">
                {sortedActivities.map((activity) => {
                    if (activity.type === 'comment_added') {
                        return (
                            <div key={activity.id} className="bg-neutral-200/50 dark:bg-muted/40 rounded-lg p-4 space-y-2">
                                <div className="flex gap-2">
                                    <div className="flex-shrink-0">
                                        <div className={`w-4 h-4 rounded-full ${activity.actor.avatarColor} flex items-center justify-center`}>
                                            <span className="text-[0.6rem] font-medium text-white">
                                                {activity.actor.initials}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-baseline gap-2 text-xs text-neutral-500 dark:text-neutral-500">
                                        <span className="font-medium text-xs text-neutral-900 dark:text-neutral-200">
                                            {activity.actor.name}
                                        </span>
                                        <span className="text-xs">
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger className="text-xs">
                                                        {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <span className="font-medium text-xs">
                                                            {new Date(activity.createdAt).toLocaleString()}
                                                        </span>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </span>
                                    </div>
                                </div>

                                {/* <div className="text-xs text-neutral-600 dark:text-neutral-400">
                                    {activity.comment.json ? JSON.stringify(activity.comment.json, null, 2) : 'NO JSON'}
                                </div> */}
                                <CommentRenderer content={activity.comment.json} />
                            </div>
                        );
                    }

                    // Regular activity items
                    try {
                        console.log('activity:', activity);
                        const config = activityConfig[activity.type] || defaultConfig;
                        const Icon = typeof config.icon === 'function' ? config.icon(activity) : config.icon;
                        
                        return (
                            <div key={activity.id} className="flex gap-2 pl-4">
                                <div className="flex-shrink-0">
                                    {React.createElement(typeof Icon === 'function' ? Icon : Icon.type, {
                                        className: `w-4 h-4 ${
                                            activity.type === 'priority_changed' 
                                                ? priorityConfig[activity.metadata.to.toLowerCase()].color
                                                : activity.type === 'status_changed'
                                                ? statusConfig[activity.metadata.to].color
                                                : 'text-muted-foreground'
                                        }`
                                    })}
                                </div>
                                <div className="flex-grow">
                                    <p className="text-xs text-neutral-600 dark:text-neutral-400">
                                        <span className="font-medium text-neutral-700 dark:text-neutral-200">
                                            {activity.actor.name}
                                            {activity.type === 'task_created' && activity.metadata.createdFromSubmission && <> & StatusBot</>}
                                        </span>
                                        {" "}
                                        <span dangerouslySetInnerHTML={{ __html: config.getMessage(activity) }} />
                                        {" "}
                                        <span className="text-neutral-600 dark:text-neutral-500">
                                            - <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger className="text-xs">
                                                        {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        {new Date(activity.createdAt).toLocaleString()}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </span>
                                    </p>
                                </div>
                            </div>
                        );
                    } catch(e) {
                        console.error('Something went wrong while rendering this activity:', e);
                        return (
                            <div key={activity.id} className="flex gap-2 pl-4">
                                <div className="text-xs text-neutral-600 dark:text-neutral-400" data-debug-activity={JSON.stringify(activity)}>
                                    Something went wrong while rendering this activity
                                </div>
                            </div>
                        )
                    }
                })}
            </div>
        </div>
    );
} 